/**
 * Billing's API client — the ledger, invoices, credit notes, receipts and
 * expenses (spec §G, ADR-0011).
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **Every money value is a string, both ways.** `NUMERIC(14,2)` on the server,
 * a decimal string on the wire, and a string here — never a `number`. A rupee
 * amount that has been through a JavaScript `number` is a rupee amount that may
 * have been rounded, and the one place that matters is a tax invoice under a
 * firm's own GSTIN. Arithmetic on these values happens on the server; this file
 * formats them and nothing else.
 *
 * **No rule is re-derived here.** Whether this person may issue, whether they
 * may credit, whether an invoice is paid — all of it is answered by the server
 * on the payload (`can_issue`, `can_credit`, `payment_state`), because the
 * server is the thing that will refuse. Two copies of a permission rule agree
 * today and disagree in a year, and the one the firm sees would be the wrong
 * one. The shape `lib/api/cases.ts` set in Wave 3A.
 *
 * The pure helpers at the bottom live beside the client rather than inside a
 * component, so the screens' formatting can be tested without rendering
 * anything.
 */

import { ApiError, ORG_HEADER, api, getOrgId } from '../api'
import type { CheckoutDescriptor, CheckoutSession } from '../checkout'
import { SERVICE_API } from '../config'
import { session } from '../session'

// -- shapes ------------------------------------------------------------------

export type InvoiceStatus = 'draft' | 'issued' | 'cancelled'
export type PaymentState = InvoiceStatus | 'unpaid' | 'part_paid' | 'paid'
export type GstTreatment = 'taxable' | 'exempt'
export type ReceiptMode =
  | 'cheque'
  | 'cash'
  | 'bank_transfer'
  | 'neft'
  | 'rtgs'
  | 'upi'
  | 'gateway'
/** The six a person records by hand — C10, ADR-0027 §6. `gateway` is written
 *  only by a capture from the firm's payment gateway, which carries the payment
 *  id a refund needs; a hand-typed one never did. */
export type ManualReceiptMode = Exclude<ReceiptMode, 'gateway'>
export type LedgerKind =
  | 'invoice'
  | 'receipt'
  | 'advance'
  | 'advance_applied'
  | 'credit_note'
  | 'refund'
  | 'expense'
  | 'write_off'
export type Direction = 'debit' | 'credit' | 'memo'

export interface PartySnapshot {
  name: string
  address: string
  state: string
  gstin: string
  pan: string
  email: string
  mobile: string
}

export interface InvoiceLine {
  id: string
  line_no: number
  description: string
  sac_code: string | null
  quantity: string
  unit_amount: string
  taxable_value: string
  gst_treatment: GstTreatment
  gst_rate: string | null
  cgst_amount: string
  sgst_amount: string
  igst_amount: string
  line_total: string
  source: 'manual' | 'plan' | 'expense' | 'hours'
  plan_id: string | null
  expense_id: string | null
  /** w5a — which logged entry this line bills (T-3.6). `null` on every other
   *  kind of line. */
  time_entry_id: string | null
}

/** One unbilled hour, priced by the server — T-3.6.
 *
 *  `amount` is computed on the **server**, by the same function the invoice
 *  line will use, because `rate × minutes / 60` is exact in integer paise and
 *  is not exact once it has been through decimal hours. The picker shows what
 *  the line will say. */
export interface BillableTime {
  id: string
  case_id: string
  user_id: string
  spent_on: string
  minutes: number
  /** `2h 30m`, from the server — one spelling of a duration in the product. */
  duration: string
  note: string | null
  rate: string
  amount: string
}

export type RetainerCadence = 'monthly' | 'quarterly' | 'half_yearly' | 'annual'
export type RetainerStatus = 'active' | 'paused' | 'ended'

/** An invoice schedule — FR-G6. */
export interface Retainer {
  id: string
  client_id: string
  service_id: string | null
  plan_id: string | null
  description: string
  amount: string
  gst_treatment: GstTreatment
  gst_rate: string | null
  cadence: RetainerCadence
  start_date: string
  end_date: string | null
  /** The next period it owes an invoice for. `null` once it has ended. */
  next_run_on: string | null
  /** Whether the nightly run prepares a draft or issues a numbered invoice. */
  issue_mode: 'draft' | 'issue'
  status: RetainerStatus
  last_run_at: string | null
}

/** What one pass of the retainer run did. */
export interface RetainerRun {
  due: number
  drafted: number
  issued: number
  already_raised: number
  ended: number
  failed: number
  /** Schedules with periods still owed after this pass's per-schedule cap. */
  outstanding: number
}

/** An approved plan or service change, and the re-pricing it owes — FR-E7. */
export interface CaseChangeAdjustment {
  id: string
  case_id: string
  case_number: string | null
  client_id: string
  kind: 'plan' | 'service'
  from_id: string | null
  to_id: string
  reason: string
  decided_at: string | null
  adjustment: { kind: 'invoice' | 'credit_note'; id: string } | null
  /** `null` when the change is not priceable — see `owes`. */
  delta: string | null
  /** `invoice` when the case got dearer, `credit_note` when it got cheaper,
   *  `nothing` when the two plans cost the same, and `manual` when ProDesk
   *  cannot work it out: a service change clears the case's plan, and a plan
   *  priced on request has no figure to compare. */
  owes: 'invoice' | 'credit_note' | 'nothing' | 'manual'
  from_amount: string | null
  to_amount: string | null
}

/** The gateway's answer about a checkout — FR-G7, ADR-0027 §2.
 *
 *  `available: false` with a `reason` is the answer for a firm that has not
 *  connected Razorpay in the console, and for a call the provider refused
 *  (`detail` is then its own words) or did not answer. When available,
 *  **`descriptor` is what is drawn** (`lib/checkout.ts`): a Razorpay order
 *  opens Checkout in the page, a hosted URL is navigated to. `checkout_url` is
 *  the second kind's URL and `null` for the first — w11a. */
export interface PaymentSession extends CheckoutSession {
  available: boolean
  reason: string | null
  detail: string | null
  session_id: string | null
  checkout_url: string | null
  descriptor: CheckoutDescriptor | Record<string, unknown>
  provider: string | null
  expires_at: string | null
  invoice_id: string
  amount: string
  currency: string
  /** The firm's name as printed on the invoice — what Checkout wears. */
  firm_name: string
  description: string
  /** The invoice's client, in Checkout's own spelling (`contact`). */
  prefill: { name?: string; email?: string; contact?: string }
}

/** What a refund did — FR-E13. The two halves are separate because they are
 *  separate facts: `from_advance` reversed nothing, `from_invoiced` has a
 *  credit note against it. */
export interface RefundResult {
  /** w11b (C10, ADR-0027 §5). `settled` — a manual refund, written now.
   *  `requested` — a gateway receipt's refund, asked of the gateway and written
   *  to the ledger when the gateway confirms the money has gone back; until
   *  then `credit_note` is null and the balance has not moved. */
  state: 'settled' | 'requested'
  receipt: Receipt
  amount: string
  from_advance: string
  from_invoiced: string
  credit_note: CreditNote | null
  invoice: Invoice | null
  gateway_reversed: boolean
  refund_request: GatewayRefundRequest | null
  balance: string
}

/** A refund asked of the payment gateway and not yet settled — ADR-0027 §5. */
export interface GatewayRefundRequest {
  id: string
  receipt_id: string
  status: 'requested' | 'settled' | 'refused'
  amount: string
  from_advance: string
  from_invoiced: string
  invoice_id: string | null
  requested_at: string | null
  settled_at: string | null
  credit_note_id: string | null
  /** Why it has not settled, when something stopped it — the gateway's
   *  refusal, or a settlement that could not be written. */
  last_error: string | null
}

export interface Invoice {
  id: string
  /** `null` until it is issued — a draft holds no number, so the firm's series
   *  has no gaps for documents that never went out. */
  number: string | null
  fy: string | null
  status: InvoiceStatus
  origin: 'manual' | 'action' | 'retainer'
  client_id: string
  case_id: string | null
  issue_date: string | null
  due_date: string | null
  place_of_supply: string | null
  is_interstate: boolean
  /** C27, ADR-0056 §4 — the tax is payable by the client under reverse charge:
   *  computed and shown (`total_tax`), **not charged**. `grand_total` and
   *  `outstanding` are the server's and already leave it out. Optional because
   *  an answer from before migration `0047` does not carry it. */
  reverse_charge?: boolean
  /** The GST the client pays the government itself on a reverse-charge
   *  invoice; `"0.00"` on every other one. */
  tax_payable_by_recipient?: string
  currency: string
  subtotal: string
  cgst_amount: string
  sgst_amount: string
  igst_amount: string
  total_tax: string
  round_off: string
  grand_total: string
  amount_paid: string
  outstanding: string
  /** Answered by the server, never re-derived from `amount_paid`. */
  payment_state: PaymentState
  rounding_mode: string
  notes: string | null
  terms: string | null
  firm_snapshot: Partial<PartySnapshot>
  client_snapshot: Partial<PartySnapshot>
  issued_at: string | null
  cancelled_at: string | null
  credit_note_id: string | null
  /** Set when this invoice *is* the re-pricing of an approved plan change
   *  (FR-E7, T-3.11). */
  case_change_id: string | null
  lines?: InvoiceLine[]
}

export interface CreditNote {
  id: string
  number: string
  fy: string
  client_id: string
  invoice_id: string
  invoice_number: string
  scope: 'full' | 'partial'
  reason: string
  issue_date: string
  place_of_supply: string | null
  is_interstate: boolean
  subtotal: string
  cgst_amount: string
  sgst_amount: string
  igst_amount: string
  total_tax: string
  round_off: string
  grand_total: string
  lines: Array<Record<string, string>>
}

export interface InvoiceDetail extends Invoice {
  lines: InvoiceLine[]
  credit_notes: CreditNote[]
  allocations: Array<{ id: string; receipt_id: string; amount: string }>
  can_issue: boolean
  can_credit: boolean
}

export interface Receipt {
  id: string
  number: string
  fy: string
  client_id: string
  mode: ReceiptMode
  mode_label: string
  amount: string
  /** The advance: the part of this receipt no invoice has claimed (FR-G5). */
  unapplied_amount: string
  received_on: string
  reference: string | null
  instrument_date: string | null
  bank_name: string | null
  notes: string | null
  allocations?: Array<{ id: string; invoice_id: string; amount: string }>
  /** w11b — whether a refund of this receipt goes back through the gateway:
   *  true for a capture's receipt, false for every manual one, including a
   *  `gateway` receipt typed by hand before C10 (ADR-0027 §6). */
  gateway_payment: boolean
  /** How much more a capture brought than its invoice owed — ADR-0027 §4's
   *  overpayment flag. `null` when it did not overpay. */
  overpaid_amount: string | null
  /** When the gateway reported a dispute on this payment. A flag only: a
   *  dispute moves no money (ADR-0027 §4). */
  disputed_at: string | null
  /** The refund waiting on the gateway, while there is one. */
  refund_requested: GatewayRefundRequest | null
}

export interface Expense {
  id: string
  client_id: string
  case_id: string
  description: string
  amount: string
  incurred_on: string
  vendor: string | null
  reference: string | null
  is_billable: boolean
  gst_treatment: GstTreatment
  gst_rate: string | null
  sac_code: string | null
  invoice_id: string | null
  notes: string | null
}

export interface LedgerEntry {
  id: string
  kind: LedgerKind
  direction: Direction
  amount: string
  debit: string | null
  credit: string | null
  entry_date: string
  narration: string
  source_kind: string | null
  source_id: string | null
  case_id: string | null
  running_balance?: string
}

export interface Statement {
  client_id: string
  client_name: string
  from: string
  to: string
  opening: string
  closing: string
  total_debit: string
  total_credit: string
  advance_held: string
  entries: LedgerEntry[]
}

export interface BillingVocabulary {
  receipt_modes: ReceiptMode[]
  /** w11b — the modes the *Record a receipt* form offers (ADR-0027 §6). */
  manual_receipt_modes: ManualReceiptMode[]
  invoice_statuses: InvoiceStatus[]
  ledger_kinds: LedgerKind[]
  gst_treatments: GstTreatment[]
  rounding_modes: string[]
  rounding: string
  default_place_of_supply: string | null
  /** C27, ADR-0056 §1 — the firm's state as the tax split reads it: the
   *  profile's, else its GSTIN's. `null` when neither says, and then a stated
   *  place of supply is refused (`firm_state_unknown`). Absent from an answer
   *  that predates it, when the screen falls back to the profile. */
  firm_supply_state?: string | null
  /** C27 — the state list the place of supply is chosen from, with each
   *  state's GST code. The screens draw `@fiveox/ui`'s `INDIAN_STATES`, the
   *  same list the service resolves against. */
  places_of_supply?: Array<{ name: string; code: string }>
  /** C27, ADR-0056 §4 — off unless a service says otherwise. */
  reverse_charge_default?: boolean
  invoice_series_prefix: string
  financial_year: string
  can_view_ledger: boolean
  can_create: boolean
  can_update: boolean
  /** Who the firm has said may issue — spec §P's approver role, resolved by the
   *  server. The screen shows the role's name rather than a bare 403. */
  issue_approver_role: string
  /** The role's display name, served from `app/rbac.py` so the web holds no
   *  second copy of the six names. */
  issue_approver_role_name: string
  can_issue: boolean
  credit_approver_role: string
  credit_approver_role_name: string
  can_credit: boolean
  /** w5a — the third money action (FR-E13). */
  refund_approver_role: string
  refund_approver_role_name: string
  can_refund: boolean
  retainer_cadences: RetainerCadence[]
  retainer_statuses: RetainerStatus[]
  retainer_issue_modes: Array<'draft' | 'issue'>
  /** FR-A5's switch, answered here so a billing screen does not have to read
   *  the firm's settings to know whether to offer a Time section. */
  hours_price_invoices: boolean
  /** **[gated: spec §9-2]** — `false` for every firm today, and the whole of
   *  "hide the pay button". `gateway_reason` is the sentence beside the
   *  absence: "the firm never turned it on" and "the subscription lapsed" are
   *  two different conversations with support. */
  gateway_active: boolean
  gateway_reason: string | null
}

/** Which logged time goes on a draft, and what tax it carries — T-3.6.
 *
 *  The tax is a field because there is nowhere to look it up from: FR-A5 has
 *  the hours *switch* and no rate and no treatment (spec §9-22). Left out, the
 *  server takes the case's own plan pricing; with no priced plan it refuses and
 *  names both ways forward rather than guessing a GST rate onto a real
 *  invoice. */
export interface HoursDraft {
  time_entry_ids: string[]
  gst_treatment?: GstTreatment
  gst_rate?: string | null
}

export interface LineDraft {
  description: string
  unit_amount: string
  quantity?: string
  sac_code?: string | null
  gst_treatment?: GstTreatment
  gst_rate?: string | null
  plan_id?: string | null
}

// -- the client --------------------------------------------------------------

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const out = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') out.set(key, String(value))
  }
  const text = out.toString()
  return text ? `?${text}` : ''
}

const BASE = '/api/v1/billing'

export const billing = {
  vocabulary: () => api<BillingVocabulary>(`${BASE}/vocabulary`),

  invoices: (filters: {
    client_id?: string
    case_id?: string
    status?: InvoiceStatus
    unpaid?: boolean
    q?: string
    limit?: number
    offset?: number
  } = {}) => api<Invoice[]>(`${BASE}/invoices${query(filters)}`),

  invoice: (id: string) => api<InvoiceDetail>(`${BASE}/invoices/${id}`),

  createInvoice: (body: {
    client_id: string
    case_id?: string | null
    plan_id?: string | null
    lines?: LineDraft[]
    expense_ids?: string[]
    hours?: HoursDraft
    place_of_supply?: string | null
    /** C27 — omitted, the server takes the chosen plan's service default. */
    reverse_charge?: boolean
    due_date?: string | null
    notes?: string | null
    terms?: string | null
  }) => api<Invoice>(`${BASE}/invoices`, { method: 'POST', body }),

  updateInvoice: (
    id: string,
    body: {
      lines?: LineDraft[]
      expense_ids?: string[]
      hours?: HoursDraft
      place_of_supply?: string
      reverse_charge?: boolean
      due_date?: string
      notes?: string
      terms?: string
      case_id?: string
      clear_case?: boolean
      clear_due_date?: boolean
    },
  ) => api<Invoice>(`${BASE}/invoices/${id}`, { method: 'PATCH', body }),

  deleteInvoice: (id: string) =>
    api<{ deleted: string }>(`${BASE}/invoices/${id}`, { method: 'DELETE' }),

  /**
   * Issue — irreversible, and behind step-up (FR-G2).
   *
   * `stepUpAssertion` is threaded through to `X-StepUp-Assertion`. **Absent on
   * the first try** (C27, ADR-0056 §7): the screen sends, and steps up only
   * when the server answers `STEPUP_REQUIRED` (`useStepUp`). That is not a
   * retry-without — the order is *without, then with*, and the server is the
   * gate either way, so an issue never goes through un-verified where step-up
   * is enforced.
   */
  issue: (
    id: string,
    stepUpAssertion: string | undefined,
    body: { issue_date?: string; due_date?: string } = {},
  ) =>
    api<Invoice>(`${BASE}/invoices/${id}/issue`, {
      method: 'POST',
      body,
      stepUpAssertion,
    }),

  creditNote: (
    invoiceId: string,
    stepUpAssertion: string | undefined,
    body: {
      reason: string
      lines?: Array<{ line_id: string; taxable_value?: string }>
      issue_date?: string
    },
  ) =>
    api<{ credit_note: CreditNote; invoice: Invoice }>(
      `${BASE}/invoices/${invoiceId}/credit-notes`,
      { method: 'POST', body, stepUpAssertion },
    ),

  receipts: (filters: {
    client_id?: string
    mode?: ReceiptMode
    with_advance?: boolean
    limit?: number
    offset?: number
  } = {}) => api<Receipt[]>(`${BASE}/receipts${query(filters)}`),

  receipt: (id: string) => api<Receipt>(`${BASE}/receipts/${id}`),

  recordReceipt: (body: {
    client_id: string
    mode: ReceiptMode
    amount: string
    received_on?: string
    reference?: string | null
    instrument_date?: string | null
    bank_name?: string | null
    notes?: string | null
    allocations?: Array<{ invoice_id: string; amount: string }>
  }) => api<Receipt>(`${BASE}/receipts`, { method: 'POST', body }),

  expenses: (filters: {
    client_id?: string
    case_id?: string
    billable_unbilled?: boolean
    limit?: number
  } = {}) => api<Expense[]>(`${BASE}/expenses${query(filters)}`),

  recordExpense: (body: {
    client_id: string
    case_id: string
    description: string
    amount: string
    incurred_on?: string
    vendor?: string | null
    reference?: string | null
    is_billable?: boolean
    gst_treatment?: GstTreatment
    gst_rate?: string | null
    sac_code?: string | null
    notes?: string | null
  }) => api<Expense>(`${BASE}/expenses`, { method: 'POST', body }),

  ledger: (clientId: string, filters: { kind?: LedgerKind; limit?: number } = {}) =>
    api<LedgerEntry[]>(`${BASE}/clients/${clientId}/ledger${query(filters)}`),

  balance: (clientId: string) =>
    api<{ client_id: string; balance: string; advance_held: string }>(
      `${BASE}/clients/${clientId}/balance`,
    ),

  statement: (clientId: string, from: string, to: string) =>
    api<Statement>(`${BASE}/clients/${clientId}/statement${query({ from, to })}`),

  // -- w5a ------------------------------------------------------------------

  /** This case's logged time that no live invoice has taken (T-3.6). */
  unbilledTime: (caseId: string, clientId: string) =>
    api<BillableTime[]>(`${BASE}/time/unbilled${query({ case_id: caseId, client_id: clientId })}`),

  retainers: (filters: { client_id?: string; status?: RetainerStatus } = {}) =>
    api<Retainer[]>(`${BASE}/retainers${query(filters)}`),

  createRetainer: (body: {
    client_id: string
    description: string
    amount: string
    cadence: RetainerCadence
    start_date: string
    end_date?: string | null
    gst_treatment?: GstTreatment
    gst_rate?: string | null
    issue_mode?: 'draft' | 'issue'
    service_id?: string | null
    plan_id?: string | null
  }) => api<Retainer>(`${BASE}/retainers`, { method: 'POST', body }),

  updateRetainer: (
    id: string,
    body: {
      description?: string
      amount?: string
      end_date?: string
      clear_end_date?: boolean
      gst_treatment?: GstTreatment
      gst_rate?: string | null
      issue_mode?: 'draft' | 'issue'
      status?: RetainerStatus
    },
  ) => api<Retainer>(`${BASE}/retainers/${id}`, { method: 'PATCH', body }),

  /** Raise everything this firm's schedules owe, now. Idempotent — pressing it
   *  twice reports `already_raised`, it does not raise a second invoice. */
  runRetainers: () => api<RetainerRun>(`${BASE}/retainers/run`, { method: 'POST' }),

  /**
   * Refund — irreversible, behind step-up, and behind the firm's
   * `refund:approve` role (FR-E13).
   *
   * `credit_lines` is required only for the part of the refund that reverses
   * invoiced money; the part that comes out of an advance reverses nothing and
   * needs no credit note. The server refuses when the two do not add up, which
   * is the check that keeps a client's balance honest.
   */
  refund: (
    receiptId: string,
    stepUpAssertion: string | undefined,
    body: {
      amount: string
      reason: string
      issue_date?: string
      invoice_id?: string | null
      credit_lines?: Array<{ line_id: string; taxable_value?: string }> | null
    },
  ) =>
    api<RefundResult>(`${BASE}/receipts/${receiptId}/refund`, {
      method: 'POST',
      body,
      stepUpAssertion,
    }),

  /** Ask for a checkout the client can pay through. **[gated: spec §9-2]** —
   *  answers `available: false` for every firm until the platform's gateway
   *  integration kind lands. */
  paymentSession: (invoiceId: string) =>
    api<PaymentSession>(`${BASE}/invoices/${invoiceId}/payment-session`, { method: 'POST' }),

  /** Approved plan and service changes still owed a re-pricing (FR-E7). */
  caseChanges: (pendingOnly = true) =>
    api<CaseChangeAdjustment[]>(`${BASE}/case-changes${query({ pending_only: pendingOnly })}`),

  recordAdjustment: (
    changeId: string,
    body: {
      invoice_id?: string | null
      credit_lines?: Array<{ line_id: string; taxable_value?: string }> | null
      issue_date?: string
      note?: string
    },
  ) =>
    api<{
      adjustment: 'invoice' | 'credit_note'
      invoice?: Invoice
      credit_note?: CreditNote
      delta: string | null
      owes: CaseChangeAdjustment['owes']
    }>(`${BASE}/case-changes/${changeId}/adjustment`, { method: 'POST', body }),
}

/**
 * A rendered PDF, as an object URL.
 *
 * **Not an `<a href>`.** The PDF routes are org-scoped and authenticated like
 * every other route, and a plain link carries neither the bearer nor
 * the org header — it would open a 401 in a new tab and look like the
 * document was missing. So it is fetched with the app's own fetcher and handed
 * to the browser as a blob.
 *
 * The caller revokes the URL when it is finished with it; an object URL that is
 * never revoked pins the whole document in memory for the life of the tab, and
 * an invoice list a firm clicks through all afternoon is exactly where that
 * accumulates.
 */
export async function pdfUrl(path: string): Promise<string> {
  const orgId = getOrgId()
  const res = await session.authed(`${SERVICE_API}${path}`, {
    headers: orgId ? { [ORG_HEADER]: orgId } : {},
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'That document could not be rendered.',
      res.status,
    )
  }
  return URL.createObjectURL(await res.blob())
}

// -- pure helpers ------------------------------------------------------------

/**
 * A rupee amount, formatted for a screen. **Takes a string and returns a
 * string** — the value is never parsed into a `number` on its way through.
 *
 * `Intl.NumberFormat` is fed the string directly, which it accepts, so the
 * lakh/crore grouping an Indian firm expects (`12,34,567.00`) comes from the
 * platform rather than from a regular expression somebody has to maintain.
 */
export function rupees(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const negative = value.trimStart().startsWith('-')
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // @ts-expect-error — `Intl` accepts a decimal string and TypeScript's lib
    // types have not caught up. Passing `Number(value)` here is the rounding
    // this whole file exists to avoid.
  }).format(value)
  return negative ? formatted : formatted
}

/** What the client's balance *means*, in the firm's words rather than a sign.
 *
 *  A negative balance is not an error to be clamped at zero: it is a client on
 *  advance, which is an ordinary and desirable state, and a screen that showed
 *  `-₹10,000.00` with no sentence beside it would generate a support ticket. */
export function balanceLabel(value: string): { text: string; tone: 'owed' | 'advance' | 'clear' } {
  const amount = value.trim()
  if (amount.startsWith('-')) {
    return { text: `${rupees(amount.slice(1))} in hand`, tone: 'advance' }
  }
  if (/^0+(\.0+)?$/.test(amount)) return { text: 'Nothing outstanding', tone: 'clear' }
  return { text: `${rupees(amount)} outstanding`, tone: 'owed' }
}

export const PAYMENT_LABELS: Record<PaymentState, string> = {
  draft: 'Draft',
  issued: 'Issued',
  cancelled: 'Cancelled',
  unpaid: 'Unpaid',
  part_paid: 'Part paid',
  paid: 'Paid',
}

export const MODE_LABELS: Record<ReceiptMode, string> = {
  cheque: 'Cheque',
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  neft: 'NEFT',
  rtgs: 'RTGS',
  upi: 'UPI',
  gateway: 'Online payment',
}

/** The modes the *Record a receipt* form offers, in the requirement's order —
 *  the server's `MANUAL_RECEIPT_MODES`. `gateway` is absent on purpose. */
export const MANUAL_RECEIPT_MODES: ManualReceiptMode[] = [
  'cheque',
  'cash',
  'bank_transfer',
  'neft',
  'rtgs',
  'upi',
]

/** The flags a receipt carries, as the words beside it — ADR-0027 §4 and §5.
 *  Read off the server's fields; nothing here decides whether a flag applies. */
export function receiptFlags(receipt: Receipt): Array<{ tone: 'warning' | 'info'; text: string }> {
  const flags: Array<{ tone: 'warning' | 'info'; text: string }> = []
  if (receipt.refund_requested) {
    flags.push({
      tone: 'info',
      text: `Refund requested — ${rupees(receipt.refund_requested.amount)}`,
    })
  }
  if (receipt.disputed_at) {
    flags.push({ tone: 'warning', text: 'Disputed at the gateway' })
  }
  if (receipt.overpaid_amount) {
    flags.push({ tone: 'warning', text: `Overpaid by ${rupees(receipt.overpaid_amount)}` })
  }
  return flags
}

/** What the screen says after a refund — ADR-0027 §5. A gateway refund is only
 *  *requested*: the money has not moved yet, so the sentence must not say it
 *  has, and nothing is recorded on the client's account until it does. */
export function refundOutcome(result: RefundResult): string {
  if (result.state === 'requested') {
    return (
      `Refund of ${rupees(result.amount)} requested from your payment gateway. ` +
      'It is recorded on the client\'s account — with its credit note, where one is due — ' +
      'when the gateway confirms the money has gone back.'
    )
  }
  return (
    `${rupees(result.amount)} refunded` +
    (result.credit_note
      ? ` — credit note ${result.credit_note.number} issued.`
      : ' from the advance held.')
  )
}

export const CADENCE_LABELS: Record<RetainerCadence, string> = {
  monthly: 'Every month',
  quarterly: 'Every quarter',
  half_yearly: 'Every six months',
  annual: 'Every year',
}

/** What a schedule will actually do at 02:00, in the firm's words. The
 *  difference matters: one of these puts a numbered tax invoice under the
 *  firm's own GSTIN into the world with nobody attached to it. */
export const ISSUE_MODE_LABELS: Record<'draft' | 'issue', string> = {
  draft: 'Prepare a draft for somebody to issue',
  issue: 'Issue it automatically',
}

/** A gateway call's refusal with **the provider's own words** when there are
 *  some — contract §4.4 says to show them on `provider_refused`. Every other
 *  reason is `gatewayReason`'s sentence. w11a; separate from `gatewayReason`
 *  so that function keeps its one-argument shape (it is mapped over lists). */
export function gatewayRefusal(reason: string | null, detail?: string | null): string {
  if (reason === 'provider_refused' && detail) return `Razorpay refused it: ${detail}`
  return gatewayReason(reason)
}

/** Why the pay button is not there, or why a gateway call did not happen. The
 *  server's own word is passed through. */
export function gatewayReason(reason: string | null): string {
  switch (reason) {
    // --- w11a: what a gateway *call* can answer (contract §4.4) -------------
    //
    // These arrive on a payment session or a refund, after the state read said
    // the gateway was active — so they are about one attempt, not about the
    // firm's setup, and each says what happened to the money.
    case 'provider_refused':
      return 'The payment gateway refused this. Nothing has been recorded.'
    case 'provider_unavailable':
      return 'The payment gateway did not answer in time. Nothing has been recorded — try again in a minute.'
    case 'already_paid':
      return 'A payment for this invoice has already gone through. The receipt will show here in a moment.'
    case 'operation_not_supported':
      return 'Your payment gateway does not offer this.'
    case 'amount_unconvertible':
    case 'validation_error':
      return 'ProDesk could not ask the payment gateway for this amount. Nothing has been recorded.'
    // --- the platform's four words (PROVIDER_CONNECTORS.md §3) --------------
    //
    // **`not_configured` changed meaning on 2026-09-22 and this is the line
    // that would have been wrong.** Until then it was this app's word for
    // *"our integrations URL is unset"* and it rendered as "could not reach the
    // platform". It is now `integration-service`'s word for *"this firm has
    // connected nothing"* — the commonest answer there is — and leaving the old
    // copy would have told every firm in the estate that the platform was down
    // when all they had done was not connect a gateway. Ours is `no_endpoint`
    // now, below, and the two never share a sentence again.
    case 'not_configured':
      return 'Your firm has not connected a payment gateway yet.'
    case 'disabled':
      return 'Your payment gateway is switched off in the platform console.'
    case 'verification_failed':
      return 'Your payment gateway is connected, but the platform cannot sign in with the details it has.'
    case 'entitlement_lapsed':
      return 'Online payment is not included in your plan.'
    // --- ours, for the answers that are about us rather than the firm -------
    case 'no_endpoint':
    case 'authority_unreachable':
      return 'ProDesk could not reach the platform to check your payment gateway.'
    // The legacy route's word, still reachable while anything reads it.
    case 'not_enabled':
      return 'Your firm has not switched on a payment gateway.'
    default:
      // The list is the integration service's, not ours. An unknown reason must
      // render a sentence rather than `undefined`, and the honest one says
      // nothing it has not been told.
      return 'Online payment is not available for your firm.'
  }
}

export const KIND_LABELS: Record<LedgerKind, string> = {
  invoice: 'Invoice',
  receipt: 'Receipt',
  advance: 'Advance held',
  advance_applied: 'Advance applied',
  credit_note: 'Credit note',
  refund: 'Refund',
  expense: 'Expense (unbilled)',
  write_off: 'Write-off',
}

/**
 * The default statement range: the firm's current financial year to date.
 *
 * April to March in India (FR-A5). Derived from a date rather than from the
 * server's `financial_year` string so that the screen has something to show
 * before the vocabulary has loaded — and the two agree, because both start from
 * the same April rule.
 */
export function financialYearToDate(today = new Date()): { from: string; to: string } {
  const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
  return { from: `${startYear}-04-01`, to: iso(today) }
}

/**
 * What the firm's next invoice will be numbered — for a screen asking the firm
 * to choose its prefix (FR-A5, FR-G2; Wave 5 close §3.5).
 *
 * The server's format is `{prefix}{fy}/INV/{seq:05d}` with the separator riding
 * on the prefix (`app/domain/ledger.py: _series_prefix`), so a firm with no
 * prefix gets `2026-27/INV/00001` and one with `KAC` gets `KAC/2026-27/INV/00001`.
 * **The default `INV` therefore reads `INV/2026-27/INV/00001`** — the doubled
 * token the Wave 5 close's walk found on a real invoice, and the single most
 * useful thing to show a firm before it starts issuing.
 *
 * This is the one number in this file derived in the browser rather than read
 * off the server, and it is derived because there is nothing to read: the
 * series does not exist until the first invoice is issued, and a firm choosing
 * a prefix is by definition doing it before then. `financialYearToDate`'s
 * comment applies — both sides start from the same April rule.
 */
export function invoiceNumberExample(prefix: string, startMonth = 4, today = new Date()): string {
  const month = today.getMonth() + 1
  const startYear = month >= startMonth ? today.getFullYear() : today.getFullYear() - 1
  const fy = `${startYear}-${`${(startYear + 1) % 100}`.padStart(2, '0')}`
  const clean = prefix.trim().toUpperCase()
  return `${clean ? `${clean}/` : ''}${fy}/INV/00001`
}

export function iso(value: Date): string {
  const month = `${value.getMonth() + 1}`.padStart(2, '0')
  const day = `${value.getDate()}`.padStart(2, '0')
  return `${value.getFullYear()}-${month}-${day}`
}

/**
 * The tax lines an invoice actually shows.
 *
 * IGST **or** CGST+SGST, never both — the place of supply decides, and showing
 * three rows with a zero in one of them is how a reader learns to stop reading
 * the tax block.
 */
export function taxRows(invoice: Invoice): Array<{ label: string; amount: string }> {
  if (invoice.is_interstate) return [{ label: 'IGST', amount: invoice.igst_amount }]
  return [
    { label: 'CGST', amount: invoice.cgst_amount },
    { label: 'SGST', amount: invoice.sgst_amount },
  ]
}

/** The sentence a reverse-charge invoice carries beside its tax — ADR-0056 §4:
 *  the GST is worked out and shown, and it is the client's to pay, not part of
 *  what they owe the firm. `null` on an ordinary invoice. */
export function reverseChargeNote(invoice: Invoice): string | null {
  if (!invoice.reverse_charge) return null
  return 'Tax payable on reverse charge: the GST above is payable by the client under reverse charge, and is not part of the amount due.'
}

/** The refusal ADR-0056 §2 adds: a place of supply was given and the firm's
 *  own state — profile, then GSTIN — is still unknown, so the server cannot
 *  say CGST + SGST or IGST and will not guess. */
export const FIRM_STATE_UNKNOWN = 'firm_state_unknown'

export function isFirmStateUnknown(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.code === 'VALIDATION_ERROR' &&
    err.details?.reason === FIRM_STATE_UNKNOWN
  )
}

/** Where the firm sets the state that decides the split. */
export const FIRM_STATE_HREF = '/settings/firm'

/**
 * Whether a draft can be issued, and the sentence for why it cannot.
 *
 * **The server has already answered `can_issue`**; what this adds is the
 * *reason*, which the server's boolean cannot carry — a firm told "you cannot
 * issue this" with no explanation goes to support, and the explanation is
 * different for a manager without the approver role than for an empty draft.
 */
export function issueBlock(
  invoice: InvoiceDetail | null,
  vocabulary: BillingVocabulary | null,
): string | null {
  if (!invoice || !vocabulary) return null
  if (invoice.status !== 'draft') return null
  if (invoice.lines.length === 0) return 'Add at least one line before issuing this invoice.'
  if (!vocabulary.can_update) return 'Your role cannot change invoices.'
  if (!vocabulary.can_issue) {
    return `Issuing an invoice needs a ${vocabulary.issue_approver_role_name}. Your firm can change who approves money actions in Settings.`
  }
  return null
}
